import {
  HHBox,
  HHStack,
  HHTableCell,
  HHTableRow,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import { LinearProgress, Link } from '@mui/material';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildIngestionDetailPath,
  FILE_PROCESSING_STATUS_LABELS,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { IngestionSummary } from '../domain/types';
import { formatCount, formatProgress } from '../utils/format.utils';
import { getStatusStyle, StatusTone } from '../utils/status-style.utils';
import { RelativeTimeCell } from './relative-time-cell';
import { StatusChip, STATUS_CHIP_TEST_ID } from './status-chip';
import { TableCard, tableCardTestIds } from './table-card';

export interface IngestionsTableProps {
  ingestions: IngestionSummary[];
  /** True while a fetch is in flight. Only blanks the table on first load. */
  isLoading?: boolean;
  /** True when the last fetch failed. Any rows already loaded stay visible. */
  hasError?: boolean;
  /** Rendered on the right of the footer, next to the "Showing N" count. */
  footer?: ReactNode;
  /** Filter controls rendered in a row above the column headers. */
  toolbar?: ReactNode;
}

const TEST_ID_PREFIX = 'pdg-ingestions-table';
const ids = tableCardTestIds(TEST_ID_PREFIX);

export const INGESTIONS_TABLE_TEST_ID = ids.table;
export const INGESTIONS_TABLE_ROW_TEST_ID_PREFIX = 'pdg-ingestions-table-row';
export const INGESTIONS_TABLE_EMPTY_TEST_ID = ids.empty;
export const INGESTIONS_TABLE_SKELETON_TEST_ID = ids.skeleton;
export const INGESTIONS_TABLE_FOOTER_TEST_ID = ids.footer;
export const INGESTIONS_TABLE_ERROR_TEST_ID = ids.error;
export const INGESTIONS_TABLE_TOOLBAR_TEST_ID = ids.toolbar;

export const INGESTIONS_TABLE_EMPTY_MESSAGE =
  'No ingestions match these filters. Widen the date range or clear a filter.';
export const INGESTIONS_TABLE_ERROR_MESSAGE =
  'Something went wrong loading ingestions.';

const COLUMN_HEADERS = [
  'Status',
  'File',
  'Provider',
  'Progress',
  'Created',
  'Updated',
];
const TIMESTAMP_CELL_WIDTH = 160;
const PROGRESS_BAR_WIDTH = 72;

/**
 * The bar takes the row's status colour so a glance across the column reads
 * the same as the chips. A failed or rejected file fills the bar: the
 * pipeline is over, and an empty track would suggest it never started.
 */
const PROGRESS_COLOR_BY_TONE: Record<
  StatusTone,
  'success' | 'error' | 'warning' | 'info' | 'inherit'
> = {
  ok: 'success',
  bad: 'error',
  warn: 'warning',
  info: 'info',
  mute: 'inherit',
};

const ProgressCell = ({
  ingestion,
}: {
  ingestion: IngestionSummary;
}): JSX.Element => {
  const { tone } = getStatusStyle(ingestion.status);
  const value =
    tone === 'bad'
      ? 100
      : formatProgress(ingestion.completedBatches, ingestion.totalBatches);

  return (
    <HHStack direction="row" alignItems="center" gap={2}>
      <HHBox sx={{ width: PROGRESS_BAR_WIDTH }}>
        <LinearProgress
          variant="determinate"
          value={value}
          color={PROGRESS_COLOR_BY_TONE[tone]}
          sx={{ borderRadius: 3, height: 5 }}
        />
      </HHBox>
      <HHTypography hhVariant="variant-bypass" variant="body2" noWrap>
        {`${ingestion.completedBatches}/${ingestion.totalBatches} batches · ${formatCount(
          ingestion.totalRowsParsed,
        )} rows`}
      </HHTypography>
    </HHStack>
  );
};

const IngestionRow = ({
  ingestion,
}: {
  ingestion: IngestionSummary;
}): JSX.Element => {
  const basePath = useBasePath();

  return (
    <HHTableRow
      hover
      data-testid={`${INGESTIONS_TABLE_ROW_TEST_ID_PREFIX}-${ingestion.eventId}`}
    >
      <HHTableCell>
        <StatusChip
          status={ingestion.status}
          label={FILE_PROCESSING_STATUS_LABELS[ingestion.status]}
          testId={`${STATUS_CHIP_TEST_ID}-${ingestion.eventId}`}
        />
      </HHTableCell>
      <HHTableCell>
        <HHTooltip hhVariant="variant-bypass" title={ingestion.fileName}>
          <Link
            component={RouterLink}
            to={`${basePath}/${buildIngestionDetailPath(ingestion.eventId)}`}
            underline="hover"
            color="text.primary"
            variant="body2"
            sx={{
              display: 'block',
              fontWeight: 500,
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ingestion.fileName}
          </Link>
        </HHTooltip>
      </HHTableCell>
      <HHTableCell>{ingestion.dataProvider ?? 'Unknown'}</HHTableCell>
      <HHTableCell>
        <ProgressCell ingestion={ingestion} />
      </HHTableCell>
      <HHTableCell>
        <RelativeTimeCell
          isoDate={ingestion.createdAt}
          width={TIMESTAMP_CELL_WIDTH}
        />
      </HHTableCell>
      <HHTableCell>
        <RelativeTimeCell
          isoDate={ingestion.updatedAt}
          width={TIMESTAMP_CELL_WIDTH}
        />
      </HHTableCell>
    </HHTableRow>
  );
};

export const IngestionsTable = ({
  ingestions,
  isLoading = false,
  hasError = false,
  footer,
  toolbar,
}: IngestionsTableProps): JSX.Element => (
  <TableCard
    testIdPrefix={TEST_ID_PREFIX}
    ariaLabel="File ingestions"
    headers={COLUMN_HEADERS}
    count={ingestions.length}
    noun={{ singular: 'ingestion', plural: 'ingestions' }}
    emptyMessage={INGESTIONS_TABLE_EMPTY_MESSAGE}
    errorMessage={INGESTIONS_TABLE_ERROR_MESSAGE}
    isLoading={isLoading}
    hasError={hasError}
    toolbar={toolbar}
    footer={footer}
  >
    {ingestions.map(ingestion => (
      <IngestionRow key={ingestion.eventId} ingestion={ingestion} />
    ))}
  </TableCard>
);
